import React, { useState } from 'react';
import { updateTenantProfile } from '@/lib/profileApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function FirstTimeUserSetup({ onComplete }) {
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await updateTenantProfile({
        display_name: displayName.trim(),
        job_title: jobTitle.trim() || null,
        company_name: companyName.trim() || null
      });

      onComplete(displayName.trim());
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError('Failed to save your information. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gradient-to-br from-[#0A1A2F] via-[#0F2744] to-[#0A1A2F] z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl">
            <CardHeader className="text-center pb-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: 360 }}
                transition={{ delay: 0.2, duration: 0.6, type: "spring" }}
                className="w-16 h-16 bg-gradient-to-br from-[#00C2C2] to-[#3FD1FF] rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>
              <CardTitle className="text-2xl font-bold text-[#0A1A2F]">Welcome to NEO GTM! 🎉</CardTitle>
              <CardDescription className="text-base">
                Let's personalize your experience. Tell us a bit about yourself.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName" className="text-[#0A1A2F] font-medium">
                    What should we call you? <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="displayName"
                    placeholder="e.g., Sarah, Mike, Dr. Chen"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={isSubmitting}
                    className="text-base"
                    autoFocus
                  />
                  <p className="text-xs text-[#5C6B7A]">
                    This is how we'll greet you throughout the app
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobTitle" className="text-[#0A1A2F] font-medium">
                    What's your job title?
                  </Label>
                  <Input
                    id="jobTitle"
                    placeholder="e.g., Account Executive, VP Sales"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    disabled={isSubmitting}
                    className="text-base"
                  />
                  <p className="text-xs text-[#5C6B7A]">
                    Your role or position in the organization
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-[#0A1A2F] font-medium">
                    Company name (optional)
                  </Label>
                  <Input
                    id="companyName"
                    placeholder="e.g., Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={isSubmitting}
                    className="text-base"
                  />
                  <p className="text-xs text-[#5C6B7A]">
                    Used in email signatures and formal communications
                  </p>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 bg-red-50 p-3 rounded-md"
                  >
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || !displayName.trim()}
                  className="w-full bg-gradient-to-r from-[#00C2C2] to-[#3FD1FF] hover:from-[#00A3A3] hover:to-[#2AB8E8] text-white font-medium py-6 text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Setting things up...
                    </>
                  ) : (
                    "Let's get started! 🚀"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}